import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {change, untouch, Field} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {checkTokenStatus} from '../shared/tokenFunctions';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ContentValid from 'material-ui/svg-icons/action/check-circle';
import ContentInvalid from 'material-ui/svg-icons/content/remove-circle';
import FuseTheme from "../global/theme";

const required = value => (value) ? undefined : 'Required';

/**
 * A SelectInput component with two Location choices with IDs, Names, Cities and States.
 *
 */
class NewTransferLocationsSelectInput extends Component {
    constructor(props) {
        super(props);

        this.state = {
            fromSelectChoices: [],
            toSelectChoices: [],
            fromSelectedId: null,
            toSelectedId: null,
            fromSelectedDba: null,
            locationsSelected: false,
            duplicateError: false
        };

        this.handleFromSelectChange = this.handleFromSelectChange.bind(this);
        this.handleToSelectChange = this.handleToSelectChange.bind(this);
    }

    componentDidMount() {
        this.setState({
            fromSelectedId: '',
            toSelectedId: ''
        });
        if (this.props.onFromSelectChange) {
            this.props.onFromSelectChange('record-form', 'location_from', '');
        }
        if (this.props.onToSelectChange) {
            this.props.onToSelectChange('record-form', 'location_to', '');
        }
        this.props.dispatch(change('record-form', 'location_from', ''));
        this.props.dispatch(change('record-form', 'location_to', ''));
        this.props.dispatch(change('record-form', 'transfer_items', []));
        this.props.dispatch(untouch('record-form', 'transfer_items'));
        this.fetchData();
    }

    fetchData() {
        // Assemble the URI
        const params = {
            filter: {},
            pagination: {page: 1, perPage: 10000},
            sort: {field: "id", order: "ASC"}
        };
        const resource = locationResources.LocationsForTransfer.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        let choices = this.assembleChoiceNames(result.data.payload);
                        this.setState({
                            fromSelectChoices: choices,
                            toSelectChoices: choices
                        });
                    }
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                        console.log(error.response.data);
                    } else if (error.request) {
                        // No response
                        console.log(error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            });
    }

    /**
     * Filter Locations with DBA Product Types for select options
     *
     * @param choices
     * @return {Array}
     */
    assembleChoiceNames(choices) {
        // Exclude any Locations without DBAs and DBA Product Types
        const locationList = [];
        for (let choice of choices) {
            const dba = choice.dba;
            if (dba && dba.product_type) {
                locationList.push(choice);
            }
        }
        // Set select label: ID - Name - City, State
        for (let choice of locationList) {
            const id = (choice.id) ? choice.id : '';
            const name = (choice.name) ? ' - ' + choice.name : '';
            const city = (choice.city) ? ' - ' + choice.city : '';
            const state = (choice.state) ? ', ' + choice.state : '';
            choice.display_name = id + name + city + state;
        }
        return locationList;
    }

    /**
     * Reset choices for Location To based on Location From selection
     */
    resetLocationToChoices(selectedDba) {
        const locationToList = [];
        const locationFromList = this.state.fromSelectChoices;

        for (let choice of locationFromList) {
            const dba = choice.dba;
            // Only include Locations with the same DBA Product Type
            if (dba && dba.product_type === selectedDba.product_type) {
                locationToList.push(choice);
            }
        }
        this.setState({
            toSelectChoices: locationToList
        });

        // Clear any previously added inventory
        this.props.dispatch(change('record-form', 'transfer_items', []));
        this.props.dispatch(untouch('record-form', 'transfer_items'));
    }

    /**
     * Handle select change event for Location From
     * @param event
     * @param index
     */
    handleFromSelectChange(event, index) {
        let selectedId = index;
        let selectedDba = null;
        // Retrieve the Location from the choices list based on the selected ID
        if (selectedId) {
            const location = this.state.fromSelectChoices.find(p => p.id === selectedId);
            if (location) {
                selectedId = location.id;
                if (this.props.onFromSelectChange) {
                    this.props.onFromSelectChange('record-form', 'location_from', selectedId);
                }
                this.props.dispatch(change('record-form', 'location_from', selectedId));
                const selectedDba = location.dba;
                if (selectedDba) {
                    this.props.dispatch(change('record-form', 'location_from_dba', selectedDba));
                    // Repopulate Location To choices with Locations with same DBA Product Type
                    this.resetLocationToChoices(selectedDba);
                }
            }
        } else {
            selectedId = '';
            if (this.props.onFromSelectChange) {
                this.props.onFromSelectChange('record-form', 'location_from', '');
            }
            this.props.dispatch(change('record-form', 'location_from', ''));
            this.props.dispatch(change('record-form', 'location_from_dba', ''));
        }
        this.setState({
            fromSelectedId: selectedId,
            fromSelectedDba: selectedDba
        }, () => {
            this.validateSelections();
        });
    }

    /**
     * Handle select change event for Location To
     * @param event
     * @param index
     */
    handleToSelectChange(event, index) {
        let selectedId = index;
        // Retrieve the Location from the choices list based on the selected ID
        if (selectedId) {
            const location = this.state.fromSelectChoices.find(p => p.id === selectedId);
            if (location) {
                selectedId = location.id;
                if (this.props.onToSelectChange) {
                    this.props.onToSelectChange('record-form', 'location_to', selectedId);
                }
                this.props.dispatch(change('record-form', 'location_to', selectedId));
            }
        } else {
            selectedId = '';
            if (this.props.onToSelectChange) {
                this.props.onToSelectChange('record-form', 'location_to', '');
            }
            this.props.dispatch(change('record-form', 'location_to', ''));
        }
        this.setState({
            toSelectedId: selectedId
        }, () => {
            this.validateSelections();
        });
    }

    noDuplicate = () => (this.state.duplicateError) ? 'Source and Destination cannot be the same Location.' : undefined;

    /**
     * Check to ensure two separate Locations are selected
     */
    validateSelections() {
        let from = this.state.fromSelectedId;
        let to = this.state.toSelectedId;

        // If from equals to:
        let isDuplicate = ((from && to) && (from === to));

        // If two Locations are selected:
        let locationsSelected = (from && to);

        this.setState({
            duplicateError: isDuplicate,
            locationsSelected: locationsSelected
        });
    }

    render() {
        const styles = {
            fieldDescription: {
                display: 'block',
                width: 'auto',
                height: '2em',
                fontSize: '12px',
                color: FuseTheme.palette.lightTextColor,
                margin: '1em 0 0 0',
            },
            fieldInstructions: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                fontWeight: 'bold',
                color: FuseTheme.palette.lightTextColor,
                margin: '2em 0 2em 0',
            },
            icon: {display: 'inline-block', fontSize: 24, marginLeft: 8, verticalAlign: 'sub'},
            iconValid: {color: FuseTheme.palette.correctColor},
            iconInvalid: {color: FuseTheme.palette.accent1Color},
        };
        return (
            <span>
                <Field name="location_from"
                       component={VirtualizedSelectInput}
                       choices={this.state.fromSelectChoices}
                       label="Select a source Location to transfer from:"
                       optionText="display_name"
                       onChange={this.handleFromSelectChange}
                       required={true}
                       validate={[required, this.noDuplicate]}
                       allowEmpty
                       options={{
                           className: "full-width",
                           clearable: false,
                           placeholder: "Select a source Location to transfer from:",
                           required: true,
                       }}
                />
                <Field name="location_to"
                       component={VirtualizedSelectInput}
                       choices={this.state.toSelectChoices}
                       label="Select a destination Location to transfer to:"
                       optionText="display_name"
                       onChange={this.handleToSelectChange}
                       required={true}
                       validate={[required, this.noDuplicate]}
                       allowEmpty
                       options={{
                           className: "full-width",
                           clearable: false,
                           placeholder: "Select a destination Location to transfer to:",
                           required: true,
                       }}
                />
                {this.state.locationsSelected && !this.state.duplicateError &&
                <span style={styles.fieldInstructions}>
                    Locations are selected.
                    <ContentValid style={{...styles.icon, ...styles.iconValid}}/>
                </span>
                }
                {this.state.locationsSelected && this.state.duplicateError &&
                <span style={styles.fieldInstructions}>
                    Source and Destination cannot be the same Location.
                    <ContentInvalid style={{...styles.icon, ...styles.iconInvalid}}/>
                </span>
                }
            </span>
        );
    }
}

NewTransferLocationsSelectInput.propTypes = {
    addField: PropTypes.bool.isRequired,
    onFromSelectChange: PropTypes.func,
    onToSelectChange: PropTypes.func,
    fromSelectedId: PropTypes.string,
    toSelectedId: PropTypes.string,
};

NewTransferLocationsSelectInput.defaultProps = {
    addField: true,
    fromSelectedId: null,
    toSelectedId: null,
};

const enhance = compose(
    connect()
);

export default enhance(NewTransferLocationsSelectInput);



// WEBPACK FOOTER //
// ./src/Fuse/components/NewTransferLocationsSelectInput.js